import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import ImageUploadField from "@/components/ImageUploadField";
import api from "@/lib/api";
import { toast } from "sonner";
import { normalizeImageUrl } from "@/lib/imageUrl";
import { Radio, Plus, ChevronRight, Play, Target, ClipboardEdit, Shield, Search, X, ArrowLeftRight, AlertTriangle, MapPin, CheckCircle2, MoreVertical, Trash2, Archive as ArchiveIcon, Copy, Eye, RotateCcw } from "lucide-react";

const GAME_TYPES = [
  ["exhibition", "Exhibition"], ["scrimmage", "Scrimmage"], ["league", "League Game"], ["tournament", "Tournament"],
  ["tournament_playoff", "Tournament Playoff"], ["provincial_championship", "Provincial Championship"],
  ["national_championship", "National Championship"], ["international", "International"],
];
const TOURNAMENT_TYPES = ["tournament", "tournament_playoff", "provincial_championship", "national_championship", "international"];
const CA_PROV = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];
const US_STATES = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"];
const inp = "w-full border border-slate-200 rounded-sm px-3 py-2 text-sm";
const EMPTY_OPP = { name: "", age_division: "", country: "Canada", province: "", state: "", city: "", organization_id: "", league: "", website: "", logo_url: "" };

export default function AdminLiveGames() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [feed, setFeed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    team_id: "", date: new Date().toISOString().slice(0, 10), time: "", location: "",
    game_type: "exhibition", home_away: "home", neutral_site: false, scoring_mode: "advanced",
    league_id: "", playoff_round: "final", tournament: { name: "", location: "", organizer: "" },
  });
  const [opponent, setOpponent] = useState(null);
  const [oppSearch, setOppSearch] = useState("");
  const [oppResults, setOppResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [createOpp, setCreateOpp] = useState(null);
  const [dupes, setDupes] = useState([]);

  const loadFeed = () => api.get("/live/feed").then(({ data }) => setFeed(data)).catch(() => setFeed({}));
  useEffect(() => {
    api.get("/live/teams").then(({ data }) => setTeams(data)).catch(() => {});
    api.get("/live/leagues", { params: { active: true } }).then(({ data }) => setLeagues(data)).catch(() => {});
    loadFeed();
  }, []);

  useEffect(() => {
    if (oppSearch.trim().length < 2) { setOppResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/admin/live/teams", { params: { q: oppSearch, archived: "active" } });
        setOppResults((data.items || []).filter((x) => x.id !== f.team_id).slice(0, 8));
      } catch { setOppResults([]); } finally { setSearching(false); }
    }, 280);
    return () => clearTimeout(t);
  }, [oppSearch, f.team_id]);

  const isTourn = TOURNAMENT_TYPES.includes(f.game_type);
  const setT = (k, v) => setF({ ...f, tournament: { ...f.tournament, [k]: v } });
  const myTeam = teams.find((t) => t.id === f.team_id);
  const homeTeam = f.home_away === "away" ? opponent : myTeam;
  const awayTeam = f.home_away === "away" ? myTeam : opponent;

  const schedule = () => {
    if (!f.team_id) return toast.error("Select the team you're scoring for");
    if (!opponent) return toast.error("Select or create an opponent team");
    if (!f.date) return toast.error("Date is required");
    if (f.game_type === "league" && !f.league_id) return toast.error("Select a league for league games");
    if (isTourn && !f.tournament.name) return toast.error("Tournament name is required");
    const body = {
      opponent_team_id: opponent.id, date: f.date, time: f.time, location: f.location,
      game_type: f.game_type, home_away: f.home_away, neutral_site: f.neutral_site,
      home_designation_source: "selected", scoring_mode: f.scoring_mode,
    };
    if (f.game_type === "league") body.league_id = f.league_id;
    if (isTourn) body.tournament = f.tournament;
    if (f.game_type === "tournament_playoff") body.playoff_round = f.playoff_round;
    setBusy(true);
    api.post(`/live/teams/${f.team_id}/games`, body)
      .then(({ data }) => { toast.success("Game created — opening the scoring console"); navigate(`/live/games/${data.id}`); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to create game"))
      .finally(() => setBusy(false));
  };

  const pickOpponent = (t) => { setOpponent(t); setOppSearch(""); setOppResults([]); };

  const openCreateOpp = () => { setCreateOpp({ ...EMPTY_OPP, name: oppSearch }); setDupes([]); };
  const runDupeCheck = async () => {
    const c = createOpp;
    if (!c.name.trim()) return toast.error("Team name is required");
    try {
      const { data } = await api.post("/admin/live/teams/check-duplicate", {
        name: c.name, age_division: c.age_division, province: c.province, state: c.state, country: c.country, city: c.city, organization_id: c.organization_id || null,
      });
      if ((data.matches || []).length) { setDupes(data.matches); return; }
      await doCreateOpp();
    } catch { toast.error("Could not check for duplicates"); }
  };
  const doCreateOpp = async () => {
    const c = createOpp;
    const body = {
      name: c.name, age_division: c.age_division || null, country: c.country,
      province: c.country === "Canada" ? c.province : null, state: c.country === "United States" ? c.state : null,
      city: c.city || null, league: c.league || null, website: c.website || null, logo_url: c.logo_url || null,
    };
    if (c.organization_id) body.organization_id = c.organization_id;
    try {
      const { data } = await api.post("/admin/live/teams", body);
      toast.success(`Opponent "${data.name}" created` + (data.ranking_eligible ? "" : " (not eligible for Canadian rankings)"));
      pickOpponent(data); setCreateOpp(null); setDupes([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create team"); }
  };

  const live = feed?.live_games || [];
  const upcoming = feed?.upcoming_games || [];
  const finals = feed?.final_scores || [];

  return (
    <AdminLayout>
      <div className="p-8 max-w-5xl" data-testid="admin-live-games">
        <div className="label-overline text-[#E63946] flex items-center gap-2"><Radio className="w-4 h-4" /> CBR Live</div>
        <h1 data-testid="admin-live-games-title" className="font-display text-4xl uppercase tracking-tighter mt-3">Live Games &amp; Scoring</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">Create a game between two CBR teams — pick your team, search or create the opponent, set Home/Away, then jump into the scoring console.</p>

        <div className="bg-white border border-slate-200 rounded-sm p-6 mt-8" data-testid="new-game-panel">
          <div className="label-overline flex items-center gap-2 mb-4"><Plus className="w-4 h-4 text-[#E63946]" /> New Game to Score</div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="label-overline text-[10px]">Your Team</label>
              <select data-testid="new-game-team" value={f.team_id} onChange={(e) => setF({ ...f, team_id: e.target.value })} className={`${inp} mt-1`}>
                <option value="">Select team…</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}{t.age_division ? ` (${t.age_division})` : ""}</option>)}
              </select>
            </div>
            <div className="relative">
              <label className="label-overline text-[10px]">Opponent</label>
              {opponent ? (
                <div data-testid="selected-opponent" className="mt-1 flex items-center gap-2 border border-slate-200 rounded-sm px-3 py-2">
                  <span className="w-6 h-6 rounded-sm bg-slate-50 border border-slate-100 grid place-items-center overflow-hidden shrink-0">
                    {opponent.effective_logo_url || opponent.logo_url ? <img src={normalizeImageUrl(opponent.effective_logo_url || opponent.logo_url)} alt="" className="w-full h-full object-contain" /> : <Shield className="w-3 h-3 text-slate-300" />}
                  </span>
                  <span className="text-sm font-semibold flex-1 truncate">{opponent.name}{opponent.age_division ? ` (${opponent.age_division})` : ""}</span>
                  {opponent.ranking_eligible === false && <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm">Intl — not ranked</span>}
                  <button data-testid="clear-opponent" onClick={() => setOpponent(null)} className="text-slate-400 hover:text-[#E63946]"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <div className="mt-1 flex items-center gap-2 border border-slate-200 rounded-sm px-3">
                    <Search className="w-4 h-4 text-slate-400" />
                    <input data-testid="opponent-search" value={oppSearch} onChange={(e) => setOppSearch(e.target.value)} placeholder="Search teams…" className="flex-1 py-2 text-sm outline-none" />
                  </div>
                  {(oppResults.length > 0 || oppSearch.length >= 2) && (
                    <div data-testid="opponent-results" className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-y-auto">
                      {searching && <div className="p-3 text-xs text-slate-400">Searching…</div>}
                      {!searching && oppResults.map((t) => (
                        <button key={t.id} data-testid={`opponent-option-${t.id}`} onClick={() => pickOpponent(t)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0">
                          <div className="text-sm font-semibold flex items-center gap-2">{t.name}{t.ranking_eligible === false && <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1 rounded-sm">Intl</span>}</div>
                          <div className="text-[11px] text-slate-500">{[t.organization_name, t.age_division, t.province || t.state, t.country].filter(Boolean).join(" · ") || "—"}</div>
                        </button>
                      ))}
                      {!searching && oppResults.length === 0 && <div className="p-3 text-xs text-slate-400">No teams found.</div>}
                      <button data-testid="create-opponent-btn" onClick={openCreateOpp} className="w-full text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#E63946] hover:bg-red-50 border-t border-slate-100 flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Create New Team</button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {myTeam && opponent && (
            <div className="mt-4 bg-slate-50 border border-slate-200 rounded-sm p-4" data-testid="matchup-panel">
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <MatchupSide label="Away" team={awayTeam} testid="away-team" />
                <span className="text-xs font-bold uppercase tracking-widest text-slate-400">at</span>
                <MatchupSide label="Home" team={homeTeam} testid="home-team" />
                <button data-testid="swap-home-away" onClick={() => setF({ ...f, home_away: f.home_away === "away" ? "home" : "away" })} className="inline-flex items-center gap-1.5 border border-slate-300 bg-white px-3 py-2 text-[10px] font-semibold uppercase tracking-wider rounded-sm hover:border-[#E63946]"><ArrowLeftRight className="w-3.5 h-3.5" /> Swap</button>
              </div>
              <div className="mt-3 flex items-center justify-center gap-4 text-xs flex-wrap">
                <button data-testid="set-myteam-home" onClick={() => setF({ ...f, home_away: "home" })} className={`px-3 py-1.5 rounded-sm font-semibold uppercase tracking-wider ${f.home_away === "home" ? "bg-[#0A192F] text-white" : "border border-slate-300"}`}>{myTeam.name} Home</button>
                <button data-testid="set-opponent-home" onClick={() => setF({ ...f, home_away: "away" })} className={`px-3 py-1.5 rounded-sm font-semibold uppercase tracking-wider ${f.home_away === "away" ? "bg-[#0A192F] text-white" : "border border-slate-300"}`}>{opponent.name} Home</button>
                <label className="flex items-center gap-1.5"><input data-testid="neutral-site-toggle" type="checkbox" checked={f.neutral_site} onChange={(e) => setF({ ...f, neutral_site: e.target.checked })} /> <MapPin className="w-3.5 h-3.5 text-slate-400" /> Neutral Site</label>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <input data-testid="new-game-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp} />
            <input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className={inp} />
            <input placeholder="Location (optional)" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={inp} />
            <select data-testid="new-game-type" value={f.game_type} onChange={(e) => {
              const gt = e.target.value;
              setF({ ...f, game_type: gt, league_id: gt === "league" ? f.league_id : "",
                tournament: TOURNAMENT_TYPES.includes(gt) ? f.tournament : { name: "", location: "", organizer: "" } });
            }} className={inp}>
              {GAME_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {f.game_type === "league" && (
              <select data-testid="new-game-league" value={f.league_id} onChange={(e) => setF({ ...f, league_id: e.target.value })} className={`${inp} sm:col-span-2`}>
                <option value="">Select league…</option>
                {leagues.map((l) => <option key={l.id} value={l.id}>{l.abbreviation ? `${l.abbreviation} — ` : ""}{l.name}</option>)}
              </select>
            )}
            {isTourn && (
              <input data-testid="new-game-tournament-name" placeholder="Tournament name" value={f.tournament.name} onChange={(e) => setT("name", e.target.value)} className={`${inp} sm:col-span-2`} />
            )}
            {f.game_type === "tournament_playoff" && (
              <select value={f.playoff_round} onChange={(e) => setF({ ...f, playoff_round: e.target.value })} className={inp}>
                {["quarterfinal", "semifinal", "final"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
          </div>

          <div className="mt-4">
            <div className="label-overline flex items-center gap-2 mb-2"><Target className="w-3.5 h-3.5 text-[#E63946]" /> Scoring Mode</div>
            <div className="flex gap-2" data-testid="new-game-scoring-mode">
              {[["basic", "Basic", "Log plate-appearance outcomes"], ["advanced", "Advanced", "Pitch-by-pitch, pitching stats, subs"]].map(([m, lbl, desc]) => (
                <button key={m} data-testid={`new-game-mode-${m}`} onClick={() => setF({ ...f, scoring_mode: m })}
                  className={`flex-1 text-left px-3 py-2 rounded-sm border transition-colors ${f.scoring_mode === m ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white border-slate-200 text-[#0A192F] hover:border-[#E63946]"}`}>
                  <div className="text-sm font-bold uppercase tracking-wider">{lbl}</div>
                  <div className={`text-[10px] mt-0.5 ${f.scoring_mode === m ? "text-white/60" : "text-slate-400"}`}>{desc}</div>
                </button>
              ))}
            </div>
          </div>

          <button data-testid="create-and-score-btn" disabled={busy} onClick={schedule}
            className="mt-5 w-full sm:w-auto flex items-center justify-center gap-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider px-6 py-3 rounded-sm hover:bg-[#c62f3b] disabled:opacity-50">
            <Play className="w-4 h-4" /> Create &amp; Open Scoring Console
          </button>
        </div>

        <div className="mt-10 space-y-8">
          <GameGroup title="Live Now" items={live} kind="live" empty="No games are live right now." onChange={loadFeed} />
          <GameGroup title="Upcoming" items={upcoming} kind="upcoming" empty="No upcoming games." onChange={loadFeed} />
          <GameGroup title="Recent Results" items={finals} kind="final" empty="No completed games yet." onChange={loadFeed} />
        </div>

        <div className="mt-10 flex flex-wrap gap-3 text-sm">
          <Link to="/admin/live/manual-entry" data-testid="link-manual-entry" className="flex items-center gap-2 text-[#0A192F] font-semibold hover:text-[#E63946]"><ClipboardEdit className="w-4 h-4" /> Manual Entry</Link>
          <Link to="/admin/live/teams" data-testid="link-cbr-teams" className="flex items-center gap-2 text-[#0A192F] font-semibold hover:text-[#E63946]"><Shield className="w-4 h-4" /> Manage Teams</Link>
        </div>
      </div>

      {createOpp && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="create-opponent-modal">
            <div className="flex items-center justify-between p-5 border-b border-slate-200"><h3 className="font-display text-xl uppercase tracking-tight">Create Opponent Team</h3><button onClick={() => { setCreateOpp(null); setDupes([]); }} className="p-2 hover:bg-slate-100"><X className="w-5 h-5" /></button></div>
            <div className="p-5 space-y-3">
              {dupes.length > 0 && (
                <div data-testid="dupe-warning" className="border-l-4 border-amber-400 bg-amber-50 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase text-amber-800"><AlertTriangle className="w-3.5 h-3.5" /> Possible duplicates found</div>
                  <div className="mt-2 space-y-1">
                    {dupes.map((m) => (
                      <button key={m.id} data-testid={`dupe-use-${m.id}`} onClick={() => { pickOpponent({ ...m, effective_logo_url: null }); setCreateOpp(null); setDupes([]); }} className="w-full text-left bg-white border border-slate-200 rounded-sm px-2 py-1.5 hover:border-[#E63946]">
                        <span className="text-sm font-semibold">{m.name}</span> <span className="text-[11px] text-slate-500">{[m.organization_name, m.age_division, m.province || m.state, m.country].filter(Boolean).join(" · ")}</span>
                      </button>
                    ))}
                  </div>
                  <button data-testid="create-anyway" onClick={doCreateOpp} className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-[#E63946]">None of these — create new anyway</button>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Team Name *"><input data-testid="opp-name" value={createOpp.name} onChange={(e) => setCreateOpp({ ...createOpp, name: e.target.value })} className={inp} /></Fld>
                <Fld label="Age Division"><input data-testid="opp-age" placeholder="e.g. 16U" value={createOpp.age_division} onChange={(e) => setCreateOpp({ ...createOpp, age_division: e.target.value })} className={inp} /></Fld>
                <Fld label="Country *"><select data-testid="opp-country" value={createOpp.country} onChange={(e) => setCreateOpp({ ...createOpp, country: e.target.value, province: "", state: "" })} className={`${inp} bg-white`}><option>Canada</option><option>United States</option><option>Other</option></select></Fld>
                {createOpp.country === "Canada" && <Fld label="Province"><select data-testid="opp-province" value={createOpp.province} onChange={(e) => setCreateOpp({ ...createOpp, province: e.target.value })} className={`${inp} bg-white`}><option value="">—</option>{CA_PROV.map((p) => <option key={p}>{p}</option>)}</select></Fld>}
                {createOpp.country === "United States" && <Fld label="State"><select data-testid="opp-state" value={createOpp.state} onChange={(e) => setCreateOpp({ ...createOpp, state: e.target.value })} className={`${inp} bg-white`}><option value="">—</option>{US_STATES.map((s) => <option key={s}>{s}</option>)}</select></Fld>}
                <Fld label="City"><input value={createOpp.city} onChange={(e) => setCreateOpp({ ...createOpp, city: e.target.value })} className={inp} /></Fld>
                <Fld label="League (optional)"><input value={createOpp.league} onChange={(e) => setCreateOpp({ ...createOpp, league: e.target.value })} className={inp} /></Fld>
                <Fld label="Website (optional)"><input value={createOpp.website} onChange={(e) => setCreateOpp({ ...createOpp, website: e.target.value })} className={inp} /></Fld>
              </div>
              <div className={`text-[11px] flex items-center gap-1.5 ${createOpp.country === "Canada" ? "text-emerald-600" : "text-amber-600"}`}>
                {createOpp.country === "Canada" ? <><CheckCircle2 className="w-3.5 h-3.5" /> Eligible for CBR Canadian rankings</> : <><AlertTriangle className="w-3.5 h-3.5" /> Not eligible for Canadian rankings — will still count toward opponents' strength of schedule</>}
              </div>
              <ImageUploadField label="Team Logo (optional)" testid="opp-logo" value={createOpp.logo_url ? normalizeImageUrl(createOpp.logo_url) : ""} onChange={(v) => setCreateOpp({ ...createOpp, logo_url: v })} aspect="aspect-square" />
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => { setCreateOpp(null); setDupes([]); }} className="px-5 py-2 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">Cancel</button>
              <button data-testid="opp-create-save" onClick={runDupeCheck} className="px-5 py-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider rounded-sm">Create &amp; Select</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function MatchupSide({ label, team, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2 min-w-[160px]">
      <span className="w-7 h-7 rounded-sm bg-slate-50 border border-slate-100 grid place-items-center overflow-hidden shrink-0">
        {team?.effective_logo_url || team?.logo_url ? <img src={normalizeImageUrl(team.effective_logo_url || team.logo_url)} alt="" className="w-full h-full object-contain" /> : <Shield className="w-3.5 h-3.5 text-slate-300" />}
      </span>
      <div><div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{label}</div><div className="text-sm font-semibold truncate max-w-[110px]">{team?.name || "—"}</div></div>
    </div>
  );
}
function Fld({ label, children }) { return <div><label className="label-overline text-[10px]">{label}</label><div className="mt-1">{children}</div></div>; }

function GameGroup({ title, items, kind, empty, onChange }) {
  return (
    <section data-testid={`admin-game-group-${kind}`}>
      <h2 className="font-display text-lg uppercase tracking-tight text-[#0A192F] mb-3">{title}</h2>
      {items.length === 0 ? <div className="bg-white border border-dashed border-slate-200 rounded-sm py-6 text-center text-sm text-slate-400">{empty}</div>
        : <div className="space-y-2">{items.map((g) => <GameRow key={g.id} g={g} kind={kind} onChange={onChange} />)}</div>}
    </section>
  );
}

function AdminGameActions({ g, kind, onChange }) {
  const [open, setOpen] = useState(false);
  const run = (p, ok) => { p.then(() => { toast.success(ok); onChange && onChange(); }).catch((e) => toast.error(e?.response?.data?.detail || "Failed")); setOpen(false); };
  const del = () => { if (window.confirm("Delete this game permanently? This cannot be undone.")) run(api.delete(`/live/games/${g.id}`), "Game deleted"); };
  const item = "w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm";
  return (
    <div className="relative shrink-0" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <button data-testid={`admin-game-actions-${g.id}`} onClick={() => setOpen(!open)} className="p-1.5 text-slate-400 hover:text-[#0A192F]"><MoreVertical className="w-4 h-4" /></button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-20 w-48 bg-white border border-slate-200 rounded-sm shadow-lg py-1" data-testid={`admin-game-menu-${g.id}`}>
            <Link to={`/live/games/${g.id}`} className={item} data-testid={`admin-game-view-${g.id}`}><Eye className="w-3.5 h-3.5" /> View</Link>
            {kind !== "final" && <Link to={`/live/games/${g.id}`} className={item} data-testid={`admin-game-continue-${g.id}`}><Play className="w-3.5 h-3.5" /> {kind === "live" ? "Continue Scoring" : "Open / Edit"}</Link>}
            <button className={item} data-testid={`admin-game-duplicate-${g.id}`} onClick={() => run(api.post(`/live/games/${g.id}/duplicate`), "Draft created")}><Copy className="w-3.5 h-3.5" /> Duplicate</button>
            {kind === "final" && <button className={item} data-testid={`admin-game-archive-${g.id}`} onClick={() => run(api.post(`/live/games/${g.id}/archive`), "Game archived")}><ArchiveIcon className="w-3.5 h-3.5" /> Archive</button>}
            <button className={`${item} text-[#E63946]`} data-testid={`admin-game-delete-${g.id}`} onClick={del}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
          </div>
        </>
      )}
    </div>
  );
}

function GameRow({ g, kind, onChange }) {
  const showScore = kind !== "upcoming";
  return (
    <div data-testid={`admin-game-row-${g.id}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm p-3 hover:border-[#E63946] transition-colors group">
      <Link to={`/live/games/${g.id}`} className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-16 text-center shrink-0">
          {kind === "live" ? <span className="text-[9px] font-bold text-white bg-[#E63946] px-1.5 py-0.5 rounded-sm animate-pulse">LIVE</span>
            : kind === "final" ? <span className="text-[10px] text-slate-500 font-semibold">FINAL</span>
            : <span className="text-[11px] text-slate-400">{(g.date || "").slice(5)}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate group-hover:text-[#E63946]">{g.team?.name || "Team"} <span className="text-slate-400">vs</span> {g.opponent?.name || "Opponent"}</div>
          <div className="text-[11px] text-slate-400 capitalize">{(g.game_type || "").replace("_", " ")}{g.location ? ` · ${g.location}` : ""}</div>
        </div>
        {showScore && <span className="font-data text-sm text-slate-500 shrink-0">{g.score?.home ?? 0}–{g.score?.away ?? 0}</span>}
      </Link>
      <AdminGameActions g={g} kind={kind} onChange={onChange} />
    </div>
  );
}
